/**
 * MANAGEMENT (14) capability keys — the EXACT cross-lab PLATFORM capability strings the super-admin
 * console gates on. These are the foundation's `PLATFORM_CAPS` (server/src/platform/rbac/catalog.js),
 * declared by the management module manifests so they enter the live capability catalog on the CLOUD
 * install. They are NOT in any of the 8 lab presets — a lab user can never hold one.
 *
 * AUTHORITY MODEL — different plane from the lab cockpit. On the server the `est_admin_plateforme`
 * flag IS the authority (`authenticatePlatform` → `authorizePlatform`), re-checked LIVE every request.
 * On the CLIENT these caps only hide/disable UI (never a trust boundary). The console's route-guard +
 * nav-guard key off `useIsPlatformAdmin()` (the session super-admin flag) — see ./session.ts. The
 * per-section caps below additionally gate which sub-surfaces a (future, scoped) operator may open.
 *
 * Verified against server/src/modules/management/{onboarding,support,subscriptions,paramtypes}.routes.js
 * and server/src/platform/rbac/catalog.js (PLATFORM_CAPS).
 */
export const MANAGEMENT_CAPS = {
  /** Read any lab cross-tenant — the lab directory + subscription directory drill-down. */
  labRead: "management.lab.read",
  /** Provision/onboard a new lab + first owner; drive lab lifecycle (actif ⇄ suspendu). */
  labOnboard: "management.lab.onboard",
  /** Publish/curate the GLOBAL analysis-parameter-type catalogue (cross-lab). */
  paramtypeManage: "management.paramtype.manage",
  /** Set/change/cancel a lab's AbonnementProjection (the on-box entitlement gate consumes it). */
  subscriptionManage: "management.subscription.manage",
  /** Triage the cross-lab support tickets + chat threads. */
  supportManage: "management.support.manage",
  /** Triage the lab-access intake funnel (LabAccessRequest → review → approve/reject → provision). */
  onboardingManage: "management.onboarding.manage",
  /** Read the DEEP cross-lab analytics plane (revenue, subscriptions, network, usage) — the Analytics section. */
  analyticsRead: "management.analytics.read",
  /** Publish/curate the pricing catalogue (PlanCatalogue) + assign plans + reconcile SubscriptionPayment. */
  pricingManage: "management.pricing.manage",
  /** Monitor the cross-lab analyzer FLEET health plane (AnalyzerHealthMetric snapshots) — the Analyzers section. */
  analyzerManage: "management.analyzer.manage",
} as const;

export type ManagementCapability = (typeof MANAGEMENT_CAPS)[keyof typeof MANAGEMENT_CAPS];

/** Every management capability — the set that, held in part or whole, identifies a platform operator. */
export const ALL_MANAGEMENT_CAPS: ReadonlyArray<ManagementCapability> = Object.values(MANAGEMENT_CAPS);
